/* Warehouse Bold — Queensland dispatch calendar for the Pilot Gear warehouse.
 *
 * Answers one question: which day does a parcel actually leave, given "now"?
 * Both the buy box's dispatch line and the delivery estimator read from here,
 * so the countdown and the estimate can never contradict each other.
 *
 * ── Why this computes instead of fetching ──────────────────────────────────
 * The obvious data sources are both traps:
 *   • data.gov.au's Australian public holidays dataset is marked INACTIVE —
 *     PM&C stopped maintaining it, the last file is 2025, and it omitted the
 *     regional show holidays even in the years it did cover.
 *   • Nager.Date gets Queensland wrong: no show holiday in any year, and it
 *     misses Boxing Day 2026 entirely (it lists only the 28th). A store
 *     trusting it would have dispatched on Boxing Day.
 * The Holidays Act 1983 (Qld) Schedule is a complete formula, so the
 * statewide dates are derivable forever with no maintenance. Validated
 * against the Queensland Government's published lists for 2026 and 2027.
 *
 * ── The one exception ──────────────────────────────────────────────────────
 * ⚠ The Gold Coast Show holiday has NO formula. It is appointed by Gazette
 * notice each year on a date the Show Society picks. "Last Friday in August"
 * looks right — and is wrong: 2023 fell on 1 September. So it is a table,
 * never a guess. See GC_SHOW below for how to extend it.
 *
 * ⚠ The warehouse is ARUNDEL 4214 — City of Gold Coast, NOT Brisbane. The
 * Ekka (Royal Queensland Show) is a Brisbane-City-only holiday and must NOT
 * appear in this calendar. Australia Post does not deliver or collect on
 * state, regional OR local public holidays, so the Gold Coast Show is a
 * genuine non-dispatch day while the Ekka is an ordinary working day here.
 */
(function (root) {
  'use strict';

  var ymd = function (y, m, d) {
    // m is 1-based. UTC throughout: these are calendar dates, not instants,
    // and local-time arithmetic would shift them across a DST boundary.
    return new Date(Date.UTC(y, m - 1, d));
  };
  var key = function (date) { return date.toISOString().slice(0, 10); };
  var addDays = function (date, n) {
    var d = new Date(date.getTime());
    d.setUTCDate(d.getUTCDate() + n);
    return d;
  };
  // 0 = Sunday … 6 = Saturday
  var dow = function (date) { return date.getUTCDay(); };

  /* Nth weekday of a month, e.g. nth(2026, 5, 1, 1) = first Monday in May. */
  var nth = function (year, month, weekday, n) {
    var d = ymd(year, month, 1);
    var shift = (weekday - dow(d) + 7) % 7;
    return addDays(d, shift + (n - 1) * 7);
  };

  /* Anonymous Gregorian computus. Easter Sunday drives Good Friday, the
     Saturday, Easter Sunday itself and Easter Monday — four QLD holidays. */
  var easterSunday = function (year) {
    var a = year % 19, b = Math.floor(year / 100), c = year % 100;
    var d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4), k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31);
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return ymd(year, month, day);
  };

  /* Gold Coast Show — GAZETTED, NOT COMPUTED.
     Source: qld.gov.au/recreation/travel/holidays/show, district
     "City of Gold Coast". Add each year as it is published (typically
     around Q1 for that year). Never extrapolate: the 2023 entry is the
     proof that the apparent pattern breaks. */
  var GC_SHOW = {
    2021: '2021-08-27',
    2022: '2022-08-26',
    2023: '2023-09-01', // ⚠ NOT the last Friday in August — the rule breaks here
    2024: '2024-08-30',
    2025: '2025-08-29',
    2026: '2026-08-28'
    // 2027: not yet gazetted at time of writing.
  };

  /* Every QLD public holiday observed at Arundel 4214 for a given year.
     Returns a Set of YYYY-MM-DD strings. */
  var forYear = function (year) {
    var out = [];
    var push = function (d) { out.push(key(d)); };

    // New Year's Day is always a holiday; a weekend one ADDS a weekday.
    var jan1 = ymd(year, 1, 1);
    push(jan1);
    if (dow(jan1) === 0) push(ymd(year, 1, 2));      // Sunday → add Mon 2 Jan
    if (dow(jan1) === 6) push(ymd(year, 1, 3));      // Saturday → add Mon 3 Jan

    // Australia Day MOVES to the Monday — the weekend day itself is not a
    // holiday. Saturday shifts 2 days, Sunday 1.
    var jan26 = ymd(year, 1, 26);
    push(dow(jan26) === 6 ? addDays(jan26, 2)
      : dow(jan26) === 0 ? addDays(jan26, 1)
        : jan26);

    // Easter: Good Friday, the Saturday, Easter Sunday, Easter Monday.
    var easter = easterSunday(year);
    push(addDays(easter, -2));
    push(addDays(easter, -1));
    push(easter);
    push(addDays(easter, 1));

    /* ⚠ Anzac Day substitutes ONLY for a Sunday, never a Saturday — this is
       where generic "weekend → Monday" logic goes wrong. 25 Apr 2026 is a
       Saturday, so 2026 has NO Anzac Monday. */
    var apr25 = ymd(year, 4, 25);
    push(dow(apr25) === 0 ? addDays(apr25, 1) : apr25);

    push(nth(year, 5, 1, 1));    // Labour Day — first Monday in May
    push(nth(year, 10, 1, 1));   // King's Birthday — first Monday in October

    // Christmas and Boxing Day are always holidays; weekend ones ADD a day.
    var dec25 = ymd(year, 12, 25);
    var dec26 = ymd(year, 12, 26);
    push(dec25);
    push(dec26);
    if (dow(dec25) === 6 || dow(dec25) === 0) push(ymd(year, 12, 27));
    if (dow(dec26) === 6 || dow(dec26) === 0) push(ymd(year, 12, 28));

    // The local one. Absent year → omitted, never guessed (see GC_SHOW).
    if (GC_SHOW[year]) out.push(GC_SHOW[year]);

    /* Christmas Eve is a PART-day holiday (6pm–midnight) that exists only for
       pay entitlements — it is a normal dispatch day and is deliberately not
       listed here. The Ekka is deliberately absent too: Brisbane only. */

    return out;
  };

  /* ============ SOUTH AUSTRALIA — the EDMO warehouse ============
     Salisbury South 5106. Products carrying `custom.dsi_type = DS1` dispatch
     from here, so they follow SA's calendar, not Queensland's.

     Verified against the Public Holidays Act 2023 (SA) — the Holidays Act
     1910 is repealed — and cross-checked date by date for 2026 and 2027.

     ⚠ Three places this differs from Queensland in ways that look like bugs:
       · ANZAC DAY NEVER SUBSTITUTES IN SA. Not for Saturday, not for Sunday.
         Queensland substitutes for a Sunday. So Mon 26 Apr 2027 is a normal
         dispatch day in Salisbury South while Arundel is shut.
       · Labour Day is the FIRST MONDAY IN OCTOBER here; in Queensland that
         same Monday is the King's Birthday. Identical dates, different names,
         and neither is a copy-paste error.
       · King's Birthday is the second Monday in JUNE, not October.
     SA's 24 and 31 December part-day holidays run 7pm–midnight and are
     deliberately omitted: they cannot affect a midday cutoff. Adelaide has no
     local show holiday, so there is no gazetted-date problem like the Gold
     Coast Show. */
  var forYearSA = function (year) {
    var out = [];
    var push = function (d) { out.push(key(d)); };

    var jan1 = ymd(year, 1, 1);
    push(jan1);
    if (dow(jan1) === 0) push(ymd(year, 1, 2));
    if (dow(jan1) === 6) push(ymd(year, 1, 3));

    var jan26 = ymd(year, 1, 26);
    push(dow(jan26) === 6 ? addDays(jan26, 2)
      : dow(jan26) === 0 ? addDays(jan26, 1)
        : jan26);

    push(nth(year, 3, 1, 2));    // Adelaide Cup Day — second Monday in March

    var easter = easterSunday(year);
    push(addDays(easter, -2));   // Good Friday
    push(addDays(easter, -1));   // Easter Saturday
    push(easter);                // Easter Sunday
    push(addDays(easter, 1));    // Easter Monday

    push(ymd(year, 4, 25));      // ANZAC Day — never substituted, see above

    push(nth(year, 6, 1, 2));    // King's Birthday — second Monday in June
    push(nth(year, 10, 1, 1));   // Labour Day — first Monday in October

    var dec25 = ymd(year, 12, 25);
    var dec26 = ymd(year, 12, 26);   // Proclamation Day
    push(dec25);
    push(dec26);
    if (dow(dec25) === 6 || dow(dec25) === 0) push(ymd(year, 12, 27));
    if (dow(dec26) === 6 || dow(dec26) === 0) push(ymd(year, 12, 28));

    return out;
  };

  var BUILDERS = { main: forYear, ds1: forYearSA };
  var norm = function (code) { return BUILDERS[code] ? code : 'main'; };

  /* ⚠ Keyed on CALENDAR + year, not year alone. Keyed on year only, the first
     warehouse to ask about 2027 would answer for both — and the SA warehouse
     would inherit Queensland's Anzac Monday. */
  var cache = {};
  var setFor = function (year, code) {
    var ck = code + ':' + year;
    if (!cache[ck]) cache[ck] = BUILDERS[code](year).reduce(function (s, k) { s[k] = true; return s; }, {});
    return cache[ck];
  };

  /* ============ OPERATIONAL CLOSURES ============
     Christmas shutdowns and the like: merchant-set, arbitrary, per warehouse,
     and impossible to compute — EDMO closed 24 Dec 2025 → 5 Jan 2026 one year
     and 24 Dec → 6 Jan the next. They arrive from the `dispatch_closure`
     metaobject via setClosures() rather than living in this file, so a new
     year's dates need an admin edit, never a deploy.
     `back` is the FIRST DAY OPEN, so the closed range is [from, back). */
  var closures = [];
  var setClosures = function (list) {
    closures = (list || []).filter(function (c) { return c && c.from && c.back; });
  };
  var isClosed = function (date, code) {
    var k = key(date);
    for (var i = 0; i < closures.length; i += 1) {
      var c = closures[i];
      var applies = !c.warehouse || !c.warehouse.length || c.warehouse.indexOf(code) !== -1;
      if (applies && k >= c.from && k < c.back) return true;
    }
    return false;
  };

  var isHoliday = function (date, code) {
    return !!setFor(date.getUTCFullYear(), norm(code))[key(date)];
  };
  var isBusinessDay = function (date, code) {
    var c = norm(code);
    var w = dow(date);
    return w !== 0 && w !== 6 && !isHoliday(date, c) && !isClosed(date, c);
  };

  /* True when the year's Gold Coast Show date is missing, i.e. the calendar
     is knowingly incomplete. Surfaced so a monitoring job can alert rather
     than the site silently over-promising by a day once a year. */
  var showDateKnown = function (year) { return Object.prototype.hasOwnProperty.call(GC_SHOW, year); };

  /* The dispatch date for an order placed at `now`.
     `now` MUST already be a Brisbane wall-clock date (see wb-brisbane below):
     a browser clock in Perth would otherwise roll the day differently.
     Before the cutoff on a business day → today; else the next business day. */
  /* ⚠ All the day-walking loops are BOUNDED. They were open `do/while`s back
     when only weekends and public holidays could block a day — at most four
     in a row. A closure can now span a fortnight, and a malformed one could
     in principle span forever, which would hang the tab rather than degrade.
     60 days is far past any real shutdown. */
  var LIMIT = 60;
  var nextOpen = function (d, code) {
    var n = 0;
    do { d = addDays(d, 1); n += 1; } while (!isBusinessDay(d, code) && n < LIMIT);
    return d;
  };

  var nextDispatch = function (now, cutoffHour, code) {
    var c = norm(code);
    var d = ymd(now.year, now.month, now.day);
    var beforeCutoff = now.hour < cutoffHour;
    if (!(isBusinessDay(d, c) && beforeCutoff)) d = nextOpen(d, c);
    return d;
  };

  /* Add N business days to a dispatch date — how a transit range becomes a
     delivery date. Day 1 is the first business day AFTER dispatch. */
  var addBusinessDays = function (date, n, code) {
    var c = norm(code);
    var d = date;
    for (var i = 0; i < n; i += 1) d = nextOpen(d, c);
    return d;
  };

  /* A calendar bound to one warehouse, so callers that know their origin can
     stop passing it on every call:
       var cal = WBHolidays.for('ds1');
       cal.nextDispatch(nowInAdelaide, 12);
     The bare top-level methods below keep working and still default to
     Arundel, so nothing that predates the second warehouse had to change. */
  var calendarFor = function (code) {
    var c = norm(code);
    return {
      code: c,
      isHoliday: function (d) { return isHoliday(d, c); },
      isBusinessDay: function (d) { return isBusinessDay(d, c); },
      nextDispatch: function (now, cutoffHour) { return nextDispatch(now, cutoffHour, c); },
      addBusinessDays: function (d, n) { return addBusinessDays(d, n, c); }
    };
  };

  /* Closures are merchant data, so they arrive from the page rather than this
     file. Read here, once, so EVERY consumer gets them without each having to
     remember — the countdown, the estimator and the cart drawer all ask this
     module whether a day is a business day, and a closure known to only one of
     them is worse than a closure known to none.
     Guarded for Node: this file is unit-tested outside a browser. */
  if (typeof document !== 'undefined') {
    try {
      var cfgEl = document.querySelector('[data-wb-ship-config]');
      if (cfgEl) {
        var cfg = JSON.parse(cfgEl.textContent);
        if (cfg && cfg.closures) setClosures(cfg.closures);
      }
    } catch (e) { /* a malformed blob must not take the calendar down */ }
  }

  root.WBHolidays = {
    forYear: forYear,
    forYearSA: forYearSA,
    isHoliday: isHoliday,
    isBusinessDay: isBusinessDay,
    nextDispatch: nextDispatch,
    addBusinessDays: addBusinessDays,
    showDateKnown: showDateKnown,
    key: key,
    for: calendarFor,
    setClosures: setClosures,
    closures: function () { return closures.slice(); },
    _internals: { easterSunday: easterSunday, nth: nth, GC_SHOW: GC_SHOW }
  };
}(typeof module !== 'undefined' && module.exports ? module.exports : window));
